import {
  auth,
  authWithAdminToken,
  authWithMerchantToken,
  authWithRefreshToken,
  authWithUserToken,
} from '@lib/api/v2';
import useAppStore from '@store/useAppStore';
import LOG_PREFIX from '@util/logger/constants';
import { createLogger } from '@util/logger/logger';
import Token, { setToken } from '@util/token';

const token = new Token();
const logger = createLogger(LOG_PREFIX.AUTH);
const LOCATION_BEFORE_REDIRECTION_KEY = 'locationBeforeRedirection';

export const getLoginRedirectPath = (defaultPath: string, allowedPrefixes: string[]) => {
  const savedPath = localStorage.getItem(LOCATION_BEFORE_REDIRECTION_KEY);
  localStorage.removeItem(LOCATION_BEFORE_REDIRECTION_KEY);

  if (!savedPath || !savedPath.startsWith('/') || savedPath.startsWith('//')) return defaultPath;
  if (savedPath.startsWith('/auth/')) return defaultPath;

  const isAllowed = allowedPrefixes.some((prefix) => savedPath.startsWith(prefix));
  return isAllowed ? savedPath : defaultPath;
};

/**
 * 토큰 재발급
 * @param tokenType
 * @returns
 */
export const patchRefresh = async (tokenType: RefreshTokenKey) => {
  const refreshConfig = token.getConfigWithRefreshToken(tokenType);
  const res = await authWithRefreshToken.patch(
    '/refresh',
    undefined,
    {
      ...refreshConfig,
      headers: {
        ...refreshConfig.headers,
        'X-Refresh-Token-Rotation': '1',
      },
    },
  );

  if (res.status === 200) {
    const newAccessToken = res.data.accessToken;
    const newRefreshToken = res.data.refreshToken;
    if (tokenType === 'adminRefreshToken') setToken('adminToken', newAccessToken);
    if (tokenType === 'merchantRefreshToken') setToken('merchantToken', newAccessToken);
    if (tokenType === 'userRefreshToken') setToken('userToken', newAccessToken);
    if (typeof newRefreshToken === 'string' && newRefreshToken) {
      setToken(tokenType, newRefreshToken);
    }
  }
  return res;
};

/**
 * 최고 관리자 지점 전환
 * @param merchantId
 * @returns
 */
export const postAdminMerchant = async (merchantId: number) => {
  const res = await authWithAdminToken.post('/admin/merchant', { merchantId });
  const { accessToken, refreshToken } = res.data;
  setToken('merchantToken', accessToken);
  setToken('merchantRefreshToken', refreshToken);
  localStorage.setItem('level', 'merchant');
  window.location.href = '/admin/dashboard';
  return res;
};

/**
 * 사용자 지점 전환
 * @param merchantId
 * @returns
 */
export const postUserMerchant = async (merchantId: number) => {
  const res = await authWithUserToken.post('/user/merchant', { merchantId });
  const { loginType, accessToken, refreshToken } = res.data;
  setToken('merchantToken', accessToken);
  setToken('merchantRefreshToken', refreshToken);
  localStorage.setItem('level', 'merchant');
  window.location.href = '/dashboard';
  return res;
};

/**
 * 사용자 로그인
 */
export const postUserLogin = async (phoneNum: string, password: string) => {
  const res = await auth.post(`/user/login`, { phoneNum, password });

  const { accessToken, refreshToken, loginType } = res.data;
  if (loginType === 'login-user') {
    setToken('userToken', accessToken);
    setToken('userRefreshToken', refreshToken);
    localStorage.setItem('level', 'user');
    window.location.href = '/select-merchant';
    return res;
  }

  setToken('merchantToken', accessToken);
  setToken('merchantRefreshToken', refreshToken);
  localStorage.setItem('level', 'merchant');
  window.location.href = '/dashboard';
  return res;
};

/**
 * 지점 관리자 및 최고 관리자 로그인
 *
 * 일반 로그인 성공 직후 패스키 등록 권유 페이지(/enroll-passkey)로 보낸다(LETMEUP-403).
 * 최종 목적지는 enroll 페이지에서 평가 후 pass-through 한다.
 */
export const postMerchantLogin = async (merchantCode: string, password: string) => {
  const res = await auth.post(`/merchant/login`, { merchantCode, password });
  if (res.status === 200) {
    // loginType: admin | merchant
    const { loginType, accessToken, refreshToken } = res.data;
    if (loginType === 'admin') {
      setToken('adminToken', accessToken);
      setToken('adminRefreshToken', refreshToken);
      localStorage.setItem('level', 'admin');
      window.location.href = '/enroll-passkey';
    }
    if (loginType === 'merchant') {
      setToken('merchantToken', accessToken);
      setToken('merchantRefreshToken', refreshToken);
      localStorage.setItem('level', 'merchant');
      window.location.href = '/enroll-passkey';
    }
  }
  return res;
};

/**
 * 사용자 로그아웃
 * @returns
 */
export const deleteLogout = async () => {
  try {
    await authWithRefreshToken.delete(`/logout`);
    logger.info('로그아웃 성공');
  } catch (error) {
    logger.warn('로그아웃 API 실패', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  } finally {
    useAppStore.getState().reset();
    localStorage.clear();
    localStorage.setItem(LOCATION_BEFORE_REDIRECTION_KEY, window.location.pathname);
    window.location.href = '/auth/login';
  }
};

/**
 * 회원 탈퇴 전 정보 확인
 * @returns loginUserId
 */
export const getUserWithdrawalCheck = () => authWithMerchantToken.get('/user/withdrawal/check');

/**
 * 회원 탈퇴 진행
 * @returns
 */
export const deleteUserWithdrawal = () => authWithMerchantToken.delete('/user/withdrawal');
